import type { RetentionGroupBy, RetentionResult } from '@growth/client';
import { useParams, useSearchParams } from 'react-router';
import { type HeatRow, Heatmap, LineChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { ChartCard, ErrorBox, SampleBadge } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtDay, fmtInt, fmtPct } from '../format.ts';
import { ciText, type RetentionKind, retentionSeries } from '../metrics.ts';
import { useApi } from '../session.tsx';

const GROUPS: { value: RetentionGroupBy; label: string }[] = [
  { value: 'none', label: 'Sin agrupar' },
  { value: 'install_date', label: 'Día de instalación' },
  { value: 'country', label: 'País' },
  { value: 'platform', label: 'Plataforma' },
  { value: 'app_version', label: 'Versión' },
  { value: 'network', label: 'Fuente' },
  { value: 'campaign', label: 'Campaña' },
  { value: 'ad_group', label: 'Grupo de anuncios' },
  { value: 'creative', label: 'Creatividad' },
  { value: 'experiment', label: 'Variante de experimento' },
];

const DAY_SETS: Record<string, number[]> = {
  d30: [0, 1, 2, 3, 7, 14, 30],
  week: [0, 1, 2, 3, 4, 5, 6, 7],
  d90: [0, 1, 3, 7, 14, 30, 60, 90],
};

const MAX_CURVES = 5;

export function RetentionPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const [sp, setSp] = useSearchParams();
  const groupBy = (sp.get('group_by') as RetentionGroupBy | null) ?? 'none';
  const kind = (sp.get('kind') as RetentionKind | null) ?? 'classic';
  const daySet = sp.get('days') && DAY_SETS[sp.get('days')!] ? sp.get('days')! : 'd30';
  const days = DAY_SETS[daySet]!;
  const setView = (k: string, v: string | null) =>
    setSp(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );
  const needsExperiment = groupBy === 'experiment' && !filters.params.experiment;

  const ret = useApi<RetentionResult>(needsExperiment ? null : `ret:${gameId}:${filters.key}:${groupBy}:${daySet}`, (api, s) =>
    api.retention(gameId, { ...filters.params, group_by: groupBy, days }, s),
  );
  const r = ret.data;
  const stale = ret.loading && !!r;
  const groups = r?.groups ?? [];
  const label = (g: string) =>
    groupBy === 'install_date' ? fmtDay(g) : groupBy === 'none' ? 'Todas las instalaciones' : g === 'unknown' ? (groupBy === 'country' ? 'desconocido' : 'sin atribuir / orgánico') : g;

  // Curves: up to 5 largest groups; colors assigned by name so an entity keeps its hue when others come and go.
  const curveGroups = groupBy === 'install_date' ? [] : [...groups].slice(0, MAX_CURVES).sort((a, b) => a.group.localeCompare(b.group));
  const heatDays = days.filter((d) => d > 0);
  const heatMax = Math.max(0.01, ...groups.flatMap((g) => g.days.filter((c) => c.day > 0 && c.mature > 0).map((c) => c[kind].rate ?? 0)));
  const heatRows: HeatRow[] = groups.map((g) => ({
    key: g.group,
    label: (
      <span className="row" style={{ flexWrap: 'nowrap' }}>
        <span className="ellipsis">{label(g.group)}</span>
        {g.sample === 'insufficient' && <SampleBadge sample={g.sample} />}
      </span>
    ),
    size: fmtInt(g.cohort_size),
    cells: heatDays.map((day) => {
      const c = g.days.find((x) => x.day === day);
      const p = c ? c[kind] : null;
      const mature = c && c.mature > 0 && p && p.rate !== null;
      return {
        value: mature ? p!.rate : null,
        text: mature ? fmtPct(p!.rate, 0) : '·',
        partial: !!mature && (c!.maturity ?? 1) < 0.5,
        tooltip: {
          title: `${label(g.group)} · D${day}`,
          rows: mature
            ? [
                { label: kind === 'classic' ? 'retención clásica' : 'retención rolling', value: fmtPct(p!.rate) },
                { label: 'jugadores retenidos', value: `${fmtInt(p!.k)} de ${fmtInt(p!.n)}` },
              ]
            : [{ label: 'aún no madura', value: '—' }],
          note: mature ? `${ciText(p)} · madurez ${fmtPct(c!.maturity, 0)}` : `Ninguna instalación tiene ${day} días completos (último día completo: ${r?.last_complete_day ?? '—'}).`,
        },
      };
    }),
  }));

  const table = {
    columns: [{ label: 'Grupo' }, { label: 'Cohorte', numeric: true }, ...days.map((d) => ({ label: `D${d}`, numeric: true }))],
    rows: groups.map((g) => [
      label(g.group),
      fmtInt(g.cohort_size),
      ...days.map((d) => {
        const c = g.days.find((x) => x.day === d);
        return c && c.mature > 0 ? `${fmtPct(c[kind].rate)} (${fmtPct(c[kind].ci_low, 0)}–${fmtPct(c[kind].ci_high, 0)}, n=${c.mature})` : '—';
      }),
    ]),
  };

  return (
    <>
      <PageHeader
        title="Retención"
        sub={r ? `Cohortes por día de instalación · último día completo ${fmtDay(r.last_complete_day)} · las celdas solo cuentan instalaciones con N días cumplidos` : undefined}
      />
      <FilterBar filters={filters} />
      <div className="toolbar" aria-label="Vista de retención">
        <label className="field">
          <span>Agrupar por</span>
          <select className="input" value={groupBy} onChange={(e) => setView('group_by', e.target.value === 'none' ? null : e.target.value)}>
            {GROUPS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Días</span>
          <select className="input" value={daySet} onChange={(e) => setView('days', e.target.value === 'd30' ? null : e.target.value)}>
            <option value="d30">D0–D30</option>
            <option value="week">Primera semana</option>
            <option value="d90">D0–D90</option>
          </select>
        </label>
        <div className="field">
          <span>Definición</span>
          <div className="segmented" role="group" aria-label="Definición de retención">
            <button type="button" aria-pressed={kind === 'classic'} onClick={() => setView('kind', null)} title="Activo exactamente el día N">
              Clásica
            </button>
            <button type="button" aria-pressed={kind === 'rolling'} onClick={() => setView('kind', 'rolling')} title="Activo el día N o cualquier día posterior">
              Rolling
            </button>
          </div>
        </div>
      </div>
      {needsExperiment && <div className="notice">Para agrupar por variante, elige primero un experimento en el filtro global.</div>}
      {ret.error && <ErrorBox error={ret.error} onRetry={ret.reload} />}

      {r && groupBy !== 'install_date' && (
        <ChartCard
          title={groupBy === 'none' ? 'Curva de retención' : `Curvas por ${GROUPS.find((g) => g.value === groupBy)?.label.toLowerCase()}`}
          subtitle={
            groupBy === 'none'
              ? 'Banda = intervalo de confianza 95 % (Wilson)'
              : `${Math.min(groups.length, MAX_CURVES)} grupos con más instalaciones${groups.length > MAX_CURVES ? ` (de ${groups.length}; el resto en el mapa de calor)` : ''}`
          }
          stale={stale}
          table={table}
        >
          {curveGroups.length ? (
            <LineChart
              ariaLabel="Curvas de retención"
              x={days}
              linearX
              formatX={(v) => `D${v}`}
              formatY={(v) => fmtPct(v, 0)}
              formatValue={(v) => fmtPct(v)}
              yCap={1}
              series={curveGroups.map((g, i) => {
                const s = retentionSeries(g, kind, i + 1, label(g.group));
                return groupBy === 'none' ? s : { ...s, band: undefined };
              })}
            />
          ) : (
            <div className="empty">Sin instalaciones nuevas con estos filtros.</div>
          )}
        </ChartCard>
      )}

      {r && (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Mapa de cohortes" subtitle={`${kind === 'classic' ? 'Clásica' : 'Rolling'} · color = retención (más oscuro = mayor) · pasa el cursor para ver IC y madurez`} stale={stale} table={table}>
            {heatRows.length ? (
              <Heatmap
                ariaLabel="Mapa de calor de retención por cohorte"
                columns={heatDays.map((d) => `D${d}`)}
                rows={heatRows}
                max={heatMax}
                groupLabel={GROUPS.find((g) => g.value === groupBy)?.label ?? 'Grupo'}
                format={(v) => fmtPct(v, 0)}
              />
            ) : (
              <div className="empty">Sin cohortes en el periodo.</div>
            )}
          </ChartCard>
        </div>
      )}
    </>
  );
}
